import { useState, useEffect } from 'react';
import { stats as statsApi } from '../api/client.js';
import DriverTable from './DriverTable.jsx';
import ScanStats from './ScanStats.jsx';
import ProblemChart from './ProblemChart.jsx';
import ExportButtons from './ExportButtons.jsx';
import CampaignComparison from './CampaignComparison.jsx';
import { ChartIcon, PackageIcon, UsersIcon, AlertIcon, ClockIcon } from '../common/icons.jsx';

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    statsApi.get()
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-[#1e40af] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (error) return <p className="text-red-600 text-center py-8 bg-red-50 rounded-xl">{error}</p>;
  if (!data) return null;

  const { progressPercent, totalClients, deliveredClients, pendingClients, problemClients,
    totalUnits, deliveredUnits, pendingUnits, avgDeliveryTimeMinutes } = data;

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-[#0f172a]">Statistiques campagne</h2>

      {/* Hero progress card with gradient */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e40af] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium opacity-80">Progression globale</span>
          <span className="text-3xl font-bold">{progressPercent}%</span>
        </div>
        <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-white rounded-full transition-all duration-700"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm opacity-90">
          <span>{deliveredUnits} / {totalUnits} unités livrées</span>
          <span>{deliveredClients} / {totalClients} clients livrés</span>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          icon={<PackageIcon size={24} className="text-[#059669]" />}
          value={deliveredUnits}
          label="Unités livrées"
          bg="bg-emerald-50"
        />
        <MetricCard
          icon={<PackageIcon size={24} className="text-[#64748b]" />}
          value={pendingUnits}
          label="Unités en attente"
          bg="bg-gray-50"
        />
        <MetricCard
          icon={<AlertIcon size={24} className="text-[#dc2626]" />}
          value={problemClients}
          label="Problèmes"
          bg="bg-red-50"
        />
        <MetricCard
          icon={<ClockIcon size={24} className="text-[#1e40af]" />}
          value={avgDeliveryTimeMinutes ? `${avgDeliveryTimeMinutes} min` : '--'}
          label="Temps moyen"
          bg="bg-blue-50"
        />
      </div>

      <DriverTable drivers={data.unitsByDriver} />
      <ScanStats scan={data.scanStats} />
      <ProblemChart problems={data.problemsByType} />
      <ExportButtons />
      <CampaignComparison />
    </div>
  );
}

/**
 * Metric card with large icon and value.
 * @param {{ icon: JSX.Element, value: string|number, label: string, bg: string }} props
 */
function MetricCard({ icon, value, label, bg }) {
  return (
    <div className={`${bg} rounded-2xl p-4 shadow-sm`}>
      <div className="mb-2">{icon}</div>
      <div className="text-2xl font-bold text-[#0f172a]">{value}</div>
      <div className="text-xs text-[#64748b] mt-0.5">{label}</div>
    </div>
  );
}
